<template lang="pug" src="./driver-bonuses-editor.pug"> </template>
<style lang="scss" src="./driver-bonuses-editor.scss" scoped> </style>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, numeric, minValue } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';
import request from '@/utils/request';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        bonusSystem: {
            title: '',
            description: '',
            bonusFrequency: '',
            bonusValue: '',
            bonusMaxAmount: '',
            bonusMaxOrderAmount: '',
            fixedBonusAmount: '',
            targetType: 'all',
            bonusType: 'count',
            skip: 0,
            startDate: new Date().format('{yyyy}-{MM}-{dd}'),
            endDate: new Date().format('{yyyy}-{MM}-{dd}'),
            takeOtherBrandOrders: false,
            isActive: false
        },
        drivers: [],
        bonuses: {
            smsTextForDriver: null,
            smsSend: false,
            smsTransliteration: false
        },
        callSigns: '',
        validateCallSignsObj: {
            duplicate: {
                array: [],
                value: ''
            },
            notFound: {
                array: [],
                value: ''
            },
            otherBonusSystem: {
                array: [],
                value: ''
            },
            success: {
                array: [],
                value: ''
            }
        },
        addBonusesResults: {
            success: [],
            notFound: [],
            duplicate: []
        },
        callSignsIsValid: false,
        canSaveBonusSystem: false,
        addBonusesProgress: false,
        showAddBonusesResults: false,
        brandId: null,
        loaded: false,
        pagination: {
            page: 1,
            totalPages: 1
        },
        startDateModal: false,
        endDateModal: false,
        removeModal: false,
        requestProcessing: false
    }),
    validations: {
        brandId: {
            required
        },
        bonusSystem: {
            title: {
                required
            },
            bonusType: {
              required
            },
            bonusFrequency: {
                required,
                minValue: minValue(1),
                numeric
            },
            bonusValue: {
                required,
                minValue: minValue(0.1),
                demical: true
            },
            bonusMaxAmount: {
                numeric
            },
            fixedBonusAmount: {
                numeric
            },
            skip: {
                numeric
            },
            startDate: {
                required
            },
            endDate: {
                required
            }
        },
        callSigns: {
            required
        }
    },
    computed: {
         ...mapState({
            brands: state => state.data.brands
        }),
        ...mapGetters(['userBrandId']),
        saveMethod() {
            return this.bonusSystem.id ? 'put' : 'post';
        },
        saveUrl() {
            const bonusId = this.bonusSystem.id;

            return bonusId ? `/admin/bonuses/drivers/${bonusId}` : '/admin/bonuses/drivers/';
        },
        minStartDate() {
            return new Date().format('{yyyy}-{MM}-{dd}');
        },
        minEndDate() {
            if (!this.bonusSystem.startDate) {
                return this.minStartDate;
            }

            return new Date(this.bonusSystem.startDate).format('{yyyy}-{MM}-{dd}');
        },
        canAddBonuses: function() {
            const successValue = [this.validateCallSignsObj.success.value, this.validateCallSignsObj.otherBonusSystem.value].join('\n');

            return this.callSigns.trim() === successValue.trim();
        },
        headers() {
            return [
                'callSign', 'receivedBonuses', 'brandOrderCount',
                'totalOrderCount', 'bonusOrderCount'
            ].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Bonuses.info.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        bonusTypes() {
            return ['count', 'distance'].map(i => ({id: i, name: this.$vuetify.lang.t('$vuetify.pages.Bonuses.types.' + i)}));
        },
        targetTypes() {
            return ['all', 'delivery', 'taxi'].map(i => ({id: i, name: this.$vuetify.lang.t('$vuetify.pages.Bonuses.targetTypes.' + i)}));
        }
    },
    watch: {
        bonusSystem: {
            handler: function() {
                this.canSaveBonusSystem = true;
            },
            deep: true
        },
        brandId() {
            this.canSaveBonusSystem = true;
        }
    },
    created() {
        this.$store.dispatch('data/getBrands');

        if (this.userBrandId) {
            this.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;
            this.loadData();
        }
    },
    methods: {
        async loadData(page = 1) {
            await request({
                method: 'get',
                url: `/admin/bonuses/drivers/${this.$route.params.id}`,
                params: { page }
            }).then(res => this.successLoad(res)).catch(error => {
                if(error.response && error.response.data && error.response.data.error) {
                    this.$store.dispatch('alert/addMessage', {
                        message: error.response.data.error
                    });
                }
            });
        },

        successLoad(res) {
            this.pagination = {
                page: res.data.page || 1,
                totalPages: res.data.totalPages || 1
            };

            const data = res.data.system || res.data;

            data.bonusValue = +data.bonusValue / 100;
            data.bonusMaxAmount = +data.bonusMaxAmount / 100;
            data.bonusMaxOrderAmount = +data.bonusMaxOrderAmount / 100;
            data.fixedBonusAmount = +data.fixedBonusAmount / 100;

            if (data.brandId) {
                this.brandId = data.brandId;
            }

            if (data.drivers) {
                this.drivers = data.drivers;
            }

            this.bonusSystem = Object.assign({}, this.bonusSystem, Object.reject(data, ['drivers']), {
                startDate: new Date(data.startDate).format('{yyyy}-{MM}-{dd}'),
                endDate: new Date(data.endDate).format('{yyyy}-{MM}-{dd}')
            });
            this.bonuses.smsTextForDriver = this.generateSmsText();

            if (this.bonusSystem.id && !this.$route.params.id) {
                this.$router.push(`/bonuses/driver/editor/${this.bonusSystem.id}`);
            }

            this.$nextTick(() => {
                this.canSaveBonusSystem = false;
                this.loaded = false;
            });
        },

        async saveBonusSystem() {
            if (this.$v.bonusSystem.$invalid || this.$v.brandId.$invalid) {
                this.$v.bonusSystem.$touch();
                this.$v.brandId.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign(this.bonusSystem, {
                brandId: this.brandId
            });

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res);
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        async validateCallSigns() {
            this.showAddBonusesResults = false;

            if (this.$v.callSigns.$invalid || this.$v.brandId.$invalid) {
                this.$v.callSigns.$touch();
                this.$v.brandId.$touch();

                return this.$nextTick(() => this.focusFirstErrorInput());
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = {
                callSigns: this.callSigns.split('\n').filter(it => !!it),
                brandId: this.brandId
            };

            this.bonuses.smsTextForDriver = this.generateSmsText();

            await request({ method: 'post', url: `/admin/bonuses/drivers/validate`, data })
                .then(res => {
                    if (res.status == 200) {
                        const data = Object.keys(this.validateCallSignsObj).map(item => ({
                            [item]: {
                                array: res.data[item],
                                value: res.data[item].length ? res.data[item].map('callSign').join('\n') : ''
                            }
                        }));

                        this.callSignsIsValid = true;
                        this.validateCallSignsObj = Object.assign(...data);
                        this.callSigns = [this.validateCallSignsObj.success.value, this.validateCallSignsObj.otherBonusSystem.value].join('\n').trim();

                        this.$store.dispatch('alert/addMessage', {
                            type: 'success',
                            message: 'success'
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        async addBonuses() {
            if (this.$v.callSigns.$invalid || this.$v.brandId.$invalid) {
                this.$v.phones.$touch();
                this.$v.brandId.$touch();

                return this.$nextTick(() => this.focusFirstErrorInput());
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const callSigns = this.callSigns.split('\n').filter(it => !!it);

            this.addBonusesProgress = true;

            while (callSigns.length) {
                const data = Object.assign(this.bonuses, {
                    callSigns: callSigns.splice(0, 100),
                    brandId: this.brandId,
                    bonusSystemForDriverId: this.bonusSystem.id
                });

                await request({ method: 'post', url: '/admin/bonuses/drivers/add', data })
                    .then(res => {
                        for (let key of Object.keys(this.addBonusesResults)) {
                            this.addBonusesResults[key] = this.addBonusesResults[key].concat(res.data[key]);
                        }
                    });
            }

            this.$nextTick(() => {
                this.requestProcessing = false;
            });

            this.loadData();
            this.clearAddBonusesForms();

            this.$store.dispatch('alert/addMessage', {
                type: 'success',
                message: 'success'
            });

            this.showAddBonusesResults = true;
        },

        clearAddBonusesForms() {
            this.bonuses = {
                smsTextForDriver: null,
                smsSend: false,
                smsTransliteration: false
            };

            this.callSigns = '';

            this.validateCallSignsObj = {
                duplicate: {
                    array: [],
                    value: ''
                },
                notFound: {
                    array: [],
                    value: ''
                },
                otherBonusSystem: {
                    array: [],
                    value: ''
                },
                success: {
                    array: [],
                    value: ''
                }
            };

            this.callSignsIsValid = false;
            this.addBonusesProgress = false;
        },

        addBonusesAgain() {
            this.showAddBonusesResults = false;

            this.addBonusesResults = {
                success: [],
                notFound: [],
                duplicate: []
            };

            this.$v.callSigns.$reset();
        },

        generateSmsText() {
            return this.$vuetify.lang.t('$vuetify.pages.Bonuses.editor.generatedSmsTextForDriver')
                .replace(/:brandName/g, Vue.filter('brand')(this.brandId))
                .replace(/:bonusValue/g, Number(this.bonusSystem.bonusValue))
                .replace(/:bonusFrequency/g, Number(this.bonusSystem.bonusFrequency));
        },

        changeStartDate() {
            if (this.bonusSystem.startDate > this.bonusSystem.endDate) {
                this.bonusSystem.endDate = this.bonusSystem.startDate;
            }
        },

        async remove() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({ method: 'delete', url: `/admin/bonuses/drivers/${this.bonusSystem.id}` })
                .then(res => {
                    if(res.status == 200) {
                        this.$router.push('/bonuses/driver');
                    }
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        }
    }
};
</script>
